import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService, { EmailTemplate as ServiceEmailTemplate } from '../services/emailService';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

type EmailTemplate = ServiceEmailTemplate;

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    subject: '',
    content: ''
  });
  const [previewData, setPreviewData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    company: 'Acme Corp',
    position: 'CEO',
    email: 'john.doe@acme.com'
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchTemplates();
    }
  }, [user]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await emailService.getTemplates();
      const sorted = [...(data || [])].sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
      setTemplates(sorted);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const extractVariables = (content: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;
    
    while ((match = variableRegex.exec(content)) !== null) {
      const variable = match[1].trim();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    
    return variables;
  };

  const processSpintax = (text: string): string => {
    // Simple spintax processor - finds {option1|option2|option3} patterns
    const spintaxRegex = /\{([^}]+)\}/g;
    
    return text.replace(spintaxRegex, (match, options) => {
      const optionArray = options.split('|');
      const randomIndex = Math.floor(Math.random() * optionArray.length);
      return optionArray[randomIndex].trim();
    });
  };

  const renderPreview = (template: EmailTemplate) => {
    let processedContent = template.content;
    let processedSubject = template.subject;
    
    // Process spintax first
    processedContent = processSpintax(processedContent);
    processedSubject = processSpintax(processedSubject);
    
    // Replace variables with preview data
    const variableMap: { [key: string]: string } = {
      'firstName': previewData.firstName,
      'first_name': previewData.firstName,
      'lastName': previewData.lastName,
      'last_name': previewData.lastName,
      'company': previewData.company,
      'position': previewData.position,
      'email': previewData.email
    };
    
    Object.entries(variableMap).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      processedContent = processedContent.replace(regex, value);
      processedSubject = processedSubject.replace(regex, value);
    });
    
    return {
      subject: processedSubject,
      content: processedContent
    };
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTemplate.name.trim() || !newTemplate.subject.trim() || !newTemplate.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      const variables = extractVariables(newTemplate.content + ' ' + newTemplate.subject);

      await emailService.createTemplate({
        ...newTemplate,
        variables
      } as EmailTemplate);

      toast.success('Template created successfully!');
      setShowCreateModal(false);
      fetchTemplates();
      
      // Reset form
      setNewTemplate({
        name: '',
        subject: '',
        content: ''
      });
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await emailService.deleteTemplate(id);

      setTemplates(templates.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template: EmailTemplate) => {
    try {
      await emailService.createTemplate({
        name: `${template.name} (Copy)`,
        subject: template.subject,
        content: template.content,
        variables: template.variables
      } as EmailTemplate);

      toast.success('Template duplicated successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Email Templates</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
        >
          Create Template
        </button>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-2">Template Features</h2>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Variables:</strong> Use <code className="bg-blue-100 px-1 rounded">{'{{firstName}}'}</code> or <code className="bg-blue-100 px-1 rounded">{'{{company}}'}</code> for personalization</p>
          <p><strong>Spintax:</strong> Use <code className="bg-blue-100 px-1 rounded">{'{'}</code>Hello|Hi|Hey{'}'} for random variations</p>
          <p><strong>Available Variables:</strong> firstName, lastName, company, position, email</p>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-semibold mb-2">No Templates Found</h2>
          <p className="text-gray-600 mb-6">
            You haven't created any email templates yet. Create your first template to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Create Your First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{template.name}</h3>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowPreviewModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      title="Preview"
                    >
                      👁️
                    </button>
                    <button
                      onClick={() => handleDuplicateTemplate(template)}
                      className="text-green-600 hover:text-green-800"
                      title="Duplicate"
                    >
                      📋
                    </button>
                    <button
                      onClick={() => template.id && handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Subject:</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{template.subject}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Content Preview:</p>
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {template.content.substring(0, 100)}...
                    </p>
                  </div>
                  
                  {(template.variables?.length || 0) > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Variables:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(template.variables || []).map((variable: string, index: number) => (
                          <span 
                            key={index}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                          >
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 flex justify-between items-center text-sm text-gray-500">
                  <span>
                    Created{' '}
                    {template.created_at
                      ? new Date(template.created_at).toLocaleDateString()
                      : 'Unknown'}
                  </span>
                  {template.id && (
                    <Link 
                      to={`/email/templates/${template.id}/edit`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New Template</h3>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateTemplate}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name*
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Cold Outreach Template"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Subject*
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={newTemplate.subject}
                    onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., {Quick question|Partnership opportunity} for {{company}}"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Content*
                  </label>
                  <textarea
                    id="content"
                    rows={12}
                    value={newTemplate.content}
                    onChange={(e) => setNewTemplate({...newTemplate, content: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`{Hi|Hello|Hey} {{firstName}},

I {noticed|saw|came across} {{company}} and was impressed by your {work|approach|results}.

I'm reaching out because {I think|I believe|it seems like} we might be able to {help|assist|support} you with...

{Would you be open to|Are you interested in|Could we} a quick {call|chat|conversation} this week?

{Best regards|Best|Thanks},
Your name`}
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Use variables like {'{{firstName}}'} and spintax like {'{option1|option2}'}
                  </p>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Preview: {selectedTemplate.name}</h3>
                <button 
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              {/* Preview Data Controls */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview Data</h4>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={previewData.firstName}
                    onChange={(e) => setPreviewData({...previewData, firstName: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={previewData.lastName}
                    onChange={(e) => setPreviewData({...previewData, lastName: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={previewData.company}
                    onChange={(e) => setPreviewData({...previewData, company: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Position"
                    value={previewData.position}
                    onChange={(e) => setPreviewData({...previewData, position: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
              
              {/* Email Preview */}
              <div className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Email Preview</span>
                    <button
                      onClick={() => setSelectedTemplate({...selectedTemplate})}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      🔄 Regenerate
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Subject:</p>
                    <p className="font-medium text-gray-900">{renderPreview(selectedTemplate).subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Content:</p>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="whitespace-pre-wrap text-sm text-gray-900">
                        {renderPreview(selectedTemplate).content}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
